import React, { useState, useEffect } from 'react'
import { makeStyles } from '@material-ui/core'
import { Alert, Pagination, SearchableInput, Typography } from 'foundations-library/components'
import OutlinedContent from './OutlinedContent'
import CallList from './CallList'
import ScriptsTable from './ScriptsTable'
import { getUsageSummary, list } from '../util/Api'
import { useStatementsContext } from 'providers'
import { LiveReceptionistUsageSummaryResponse } from 'interfaces'
import getBillingPeriodItems, { BillingPeriodItem } from '../util/getBillingPeriodItems'
import { FiltersType } from '../util/types'
import { useCursorPagination } from 'hooks'
import Filters from './Filters'
import { formatDate, formatTime } from 'formatters'
import { SlideTransition } from 'functional-foundations'

const jss = () => ({
    callHistory: {
        marginBottom: 40
    },
    title: {
        display: 'flex',
        justifyContent: 'space-between'
    },
    scriptstableWrapper: {
        marginTop: 20
    },
    scriptsTableHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        marginBottom: 20
    },
    pagination: {
        marginTop: 20
    }
})

const useStyles = makeStyles(jss)

/**
 *
 */
const CallHistory = (): JSX.Element => {
    const styles = useStyles()

    const [error, setError] = useState<string | null>(null)
    const [loading, setLoading] = useState<boolean>(false)
    const [scriptsResponse, setScriptsResponse] = useState<LiveReceptionistUsageSummaryResponse | null>(null)
    const [invoicesItems, setInvoicesItems] = useState<BillingPeriodItem[] | null>(null)
    const [billingPeriod, setBillingPeriod] = useState<string | undefined>()
    const [loadedBillingPeriod, setLoadedBillingPeriod] = useState<string | undefined>()
    const [filters, setFilters] = useState<FiltersType>({})
    const [search, setSearch] = useState('')
    const [lastUpdatedAt, setLastUpdatedAt] = useState<string | undefined>()
    const [nextBillingDate, setNextBillingDate] = useState<string | undefined>()
    const hasScripts = Boolean(scriptsResponse?.scripts.length)
    const showCalls = Boolean(billingPeriod) && !loading && loadedBillingPeriod === billingPeriod && hasScripts

    const {
        response: callsResponse,
        error: errorCalls,
        loading: loadingCalls,
        paginationProps
    } = useCursorPagination({
        fetchPage: list,
        enabled: showCalls,
        filters,
        pageSize: 25,
        errorMessage: 'Something went wrong while loading your script\'s calls'
    })

    const { items: invoices, getData: getStatements } = useStatementsContext()

    useEffect(() => {
        if (invoices) return
        let active = true
        getStatements().catch((err: Error) => {
            if (active) setError(err.message || 'Something went wrong while loading billing periods')
        })
        return () => { active = false }
    }, [invoices, getStatements])

    useEffect(() => {
        if (!lastUpdatedAt && callsResponse?.last_updated_at) {
            setLastUpdatedAt(formatTime(callsResponse?.last_updated_at, { day: '2-digit', month: '2-digit' }))
        }
        if (!nextBillingDate && callsResponse?.next_billing_date) {
            setNextBillingDate(formatDate(
                callsResponse?.next_billing_date,
                {
                    day: '2-digit',
                    month: '2-digit'
                }
            ) || undefined)
        }
    }, [callsResponse])

    useEffect(() => {
        if (invoices) {
            const items = getBillingPeriodItems(invoices)
            setInvoicesItems(items)
            if (items.length && !billingPeriod) {
                const period = JSON.parse(items[0].value)
                setFilters((current) => ({ ...current, from: period.from, to: period.to }))
                setBillingPeriod(items[0].value)
            }
        }
    }, [invoices])

    useEffect(() => {
        if (!billingPeriod) {
            setScriptsResponse(null)
            setLoading(false)
            return
        }
        let active = true
        const { from, to } = JSON.parse(billingPeriod)
        const loadScripts = async () => {
            setError(null)
            setScriptsResponse(null)
            setLoading(true)
            try {
                const response = await getUsageSummary({ start_date: from, end_date: to })
                if (active) {
                    setScriptsResponse(response)
                    if (response.scripts.length === 1) {
                        const scriptId = response.scripts[0].scriptId
                        setFilters((current) => current.scriptId === scriptId ? current : { ...current, scriptId })
                    }
                    setLoadedBillingPeriod(billingPeriod)
                }
            } catch (err: any) {
                if (active) setError(err.message || 'Something went wrong while loading your scripts')
            } finally {
                if (active) setLoading(false)
            }
        }
        loadScripts()
        return () => { active = false }
    }, [billingPeriod])

    const dateRangeBounds: Pick<FiltersType, 'from' | 'to'> | undefined =
        billingPeriod ? JSON.parse(billingPeriod) : undefined

    return <div className={styles.callHistory}>

        {error ? <Alert marginY={40}>{error}</Alert> : <></>}

        <div className={styles.title}>
            <Typography variant='subtitle1'>Call history</Typography>
            {
                <Typography variant='body3' color='neutral-300'>
                    Calls can take up to 24 hours to appear
                    {
                        lastUpdatedAt
                            ? <SlideTransition isOpen tag='span'> - Last updated {lastUpdatedAt}</SlideTransition>
                            : <></>
                    }
                </Typography>
            }
        </div>

        <OutlinedContent className={styles.scriptstableWrapper}>
            <div className={styles.scriptsTableHeader}>
                <Typography variant='subtitle2'>Minutes and billing period</Typography>
                <div>
                    {
                        invoicesItems
                            ? <SearchableInput
                                options={invoicesItems}
                                value={billingPeriod}
                                title='Select an option'
                                onChange={(value) => {
                                    setBillingPeriod(value)
                                    const period = value ? JSON.parse(value) : undefined
                                    setFilters((current) => ({ ...current, from: period?.from, to: period?.to }))
                                }}
                                label='Billing period'
                                labelPlacement='inside'
                                autoWidth
                            />
                            : <></>
                    }
                </div>
            </div>
            <Typography variant='body3' color='neutral-300'>
                Each script has its own plan minutes.&nbsp;
                {
                    nextBillingDate
                        ? <SlideTransition isOpen tag='span'>Resets {nextBillingDate}</SlideTransition>
                        : <></>
                }
            </Typography>
            <ScriptsTable
                scripts={scriptsResponse?.scripts}
                currency={scriptsResponse?.currency}
                loading={loading}
            />
        </OutlinedContent>
        {
            showCalls
                ? <>
                    <Filters
                        value={filters}
                        minDate={dateRangeBounds?.from != null ? new Date(dateRangeBounds.from * 1000) : undefined}
                        maxDate={dateRangeBounds?.to != null ? new Date(dateRangeBounds.to * 1000) : undefined}
                        search={search}
                        onSearchChange={setSearch}
                        onChange={setFilters}
                        scripts={scriptsResponse?.scripts}
                    />
                    <CallList
                        search={search}
                        loading={loadingCalls}
                        error={errorCalls}
                        items={callsResponse?.items}
                    />
                    {
                        callsResponse?.items?.length
                            ? <Pagination
                                {...paginationProps}
                                itemLabel='calls'
                                className={styles.pagination}
                            />
                            : <></>
                    }
                </>
                : <></>
        }
    </div>
}

export default CallHistory
